// Smart Daara - Payment Success Page
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { paymentAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import confetti from 'canvas-confetti';

export default function PaymentSuccess() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [payment, setPayment] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 30; // ~60s with 2s interval
    let timerId = null;

    const verifyPayment = async () => {
      // Get payment id from URL or fallback to localStorage (set by SubscriptionPage)
      let paymentId = searchParams.get('payment_id');
      if (!paymentId) {
        try { paymentId = localStorage.getItem('lastPaymentId'); } catch (e) { /* ignore */ }
      }

      if (!paymentId) {
        if (!cancelled) setStatus('success'); // No id; assume success (redirect from gateway)
        return;
      }

      try {
        const response = await paymentAPI.getStatus(paymentId);
        if (cancelled) return;
        setPayment(response.data);
        const s = response.data.status;

        if (s === 'completed') {
          setStatus('success');
          try { localStorage.removeItem('lastPaymentId'); } catch (e) { /* ignore */ }
          confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
          return;
        }
        if (s === 'failed' || s === 'cancelled') {
          setStatus('failed');
          try { localStorage.removeItem('lastPaymentId'); } catch (e) { /* ignore */ }
          return;
        }
        // still pending — retry with backoff
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setStatus('pending');
          return;
        }
        setStatus('verifying');
        timerId = setTimeout(verifyPayment, 2000);
      } catch (error) {
        console.error('Error verifying payment:', error);
        if (!cancelled) setStatus('failed');
      }
    };

    verifyPayment();
    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
  }, [searchParams]);

  const getDashboardPath = () => {
    if (user?.role === 'super_admin') return '/admin';
    return '/school';
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-0 shadow-lg">
        <CardContent className="p-8 text-center">
          {status === 'verifying' ? (
            <>
              <div className="flex justify-center mb-6">
                <Loader2 className="w-16 h-16 text-emerald-600 animate-spin" />
              </div>
              <h1 className="text-2xl font-outfit font-bold text-slate-900 mb-2">
                {language === 'fr' ? 'Vérification du paiement...' : 'Verifying payment...'}
              </h1>
              <p className="text-slate-500">
                {language === 'fr' 
                  ? 'Veuillez patienter pendant que nous vérifions votre paiement.'
                  : 'Please wait while we verify your payment.'
                }
              </p>
            </>
          ) : status === 'success' ? (
            <>
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-emerald-600" />
                </div>
              </div>
              <h1 className="text-2xl font-outfit font-bold text-slate-900 mb-2">
                {language === 'fr' ? 'Paiement réussi!' : 'Payment successful!'}
              </h1>
              <p className="text-slate-500 mb-6">
                {language === 'fr' 
                  ? 'Votre abonnement a été activé. Merci pour votre confiance!'
                  : 'Your subscription has been activated. Thank you for your trust!'
                }
              </p>
              {payment && (
                <div className="bg-emerald-50 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-emerald-800">
                    <strong>{language === 'fr' ? 'Montant:' : 'Amount:'}</strong> {payment.amount?.toLocaleString()} FCFA
                  </p>
                  <p className="text-sm text-emerald-800 mt-1">
                    <strong>{language === 'fr' ? 'Référence:' : 'Reference:'}</strong> {payment.id}
                  </p>
                </div>
              )}
              <Button 
                className="bg-emerald-700 hover:bg-emerald-800 w-full"
                onClick={() => navigate(getDashboardPath())}
              >
                {language === 'fr' ? 'Aller au tableau de bord' : 'Go to Dashboard'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          ) : status === 'pending' ? (
            <>
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center">
                  <Loader2 className="w-10 h-10 text-amber-600 animate-spin" />
                </div>
              </div>
              <h1 className="text-2xl font-outfit font-bold text-slate-900 mb-2">
                {language === 'fr' ? 'Paiement en cours...' : 'Payment in progress...'}
              </h1>
              <p className="text-slate-500 mb-6">
                {language === 'fr' 
                  ? 'Votre paiement est en cours de traitement. Vous recevrez une notification une fois confirmé.'
                  : 'Your payment is being processed. You will receive a notification once confirmed.'
                }
              </p>
              <Button 
                variant="outline"
                className="w-full"
                onClick={() => navigate(getDashboardPath())}
              >
                {language === 'fr' ? 'Retour au tableau de bord' : 'Back to Dashboard'}
              </Button>
            </>
          ) : (
            <>
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
                  <span className="text-4xl">❌</span>
                </div>
              </div>
              <h1 className="text-2xl font-outfit font-bold text-slate-900 mb-2">
                {language === 'fr' ? 'Échec du paiement' : 'Payment failed'}
              </h1>
              <p className="text-slate-500 mb-6">
                {language === 'fr' 
                  ? 'Le paiement n\'a pas abouti. Veuillez réessayer.'
                  : 'The payment did not go through. Please try again.'
                }
              </p>
              <Button 
                className="bg-emerald-700 hover:bg-emerald-800 w-full"
                onClick={() => navigate('/school/subscription')}
              >
                {language === 'fr' ? 'Réessayer' : 'Try Again'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
